import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge, Button, Dialog, IconArrowLeft, Notice, Plate, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { useCan } from "../auth.ts";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { formatDate, formatMoney, formatNumber, serverErrorText, t } from "../i18n.ts";
import { ProductSearch } from "../stock/RequestDraftPage.tsx";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import type { ProductRef } from "../stock/types.ts";
import { salesApi, type Candidate, type SalesLine, type SalesOrder } from "./api.ts";
import { candidatesQuery, SALES, salesOrderQuery, shortageQuery } from "./queries.ts";
import { orderTotal, SalesStateBadge } from "./SalesPage.tsx";

const route = getRouteApi("/app/sales/orders/$id");

/**
 * Заказ покупателя (ПС-01...ПС-09). Строки только серийного изделия с
 * версией (CAT-11). Отгрузка построчно: кладовщик сам выбирает серийник с
 * «Готової продукції» карточкой или сканом QR, система его не подставляет
 * (ПС-03); незакрытый маршрут проходит с предупреждением (H3), карантин
 * сервер не пускает (ПС-06). Отгруженное можно вернуть на «Карантин»
 * «Ремонту» (ПС-09); «Відмова покупця» и повторная отгрузка после ремонта
 * по H12.
 */
export function SalesOrderPage() {
  const { id } = route.useParams();
  const queryClient = useQueryClient();
  const can = useCan();
  const order = useQuery(salesOrderQuery(id));
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [shipLine, setShipLine] = useState<SalesLine | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const refresh = (n?: NoticeState) => {
    void queryClient.invalidateQueries({ queryKey: SALES });
    setNotice(n ?? null);
  };
  const fail = (e: unknown) => setNotice(errorNotice(e));
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn() });
  const act = (fn: () => Promise<unknown>, ok?: NoticeState) => run.mutate(fn, { onSuccess: () => refresh(ok), onError: fail });

  const o = order.data;
  const confirmed = o?.state === "CONFIRMED";
  const shortage = useQuery({ ...shortageQuery(id), enabled: confirmed });
  if (order.error) return <Notice tone="danger" title={errorNotice(order.error).title} className="page" />;
  if (!o) return null;
  const draft = o.state === "DRAFT";
  const lineName = (no: number) => {
    const l = o.lines.find((x) => x.no === no);
    return l ? `${l.productCode} ${l.productName}${l.version ? ` ${l.version}` : ""}` : String(no);
  };

  return (
    <div className="page order">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/sales/orders" aria-label={t("sales.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title w-num">{o.number}</h1>
        <SalesStateBadge state={o.state} />
      </div>

      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {shortage.data?.length ? (
        <Notice tone="warning" title={t("sales.shortage.title")} className="stock-notice">
          {shortage.data.map((s) => t("sales.shortage.line", { product: lineName(s.line), qty: s.missing })).join("; ")}
        </Notice>
      ) : null}

      <Plate cut="lg" className="order-head">
        <dl className="order-meta">
          <Meta label={t("sales.customer")} value={o.customerName} />
          <Meta label={t("sales.shipDate")} value={formatDate(o.shipDate)} />
          <Meta label={t("sales.total")} value={formatMoney(orderTotal(o), "UAH")} />
        </dl>
      </Plate>

      <section className="stock-section" aria-labelledby="sales-lines">
        <h2 id="sales-lines" className="desk-group-title">
          {t("sales.lines")}
        </h2>
        <div className="sales-lines" role="table" aria-labelledby="sales-lines">
          <div className="sales-line" role="row" data-head="">
            <span role="columnheader">{t("sales.product")}</span>
            <span role="columnheader" className="num">
              {t("sales.qty")}
            </span>
            <span role="columnheader" className="num">
              {t("sales.price")}
            </span>
            <span role="columnheader" className="num">
              {t("sales.remaining")}
            </span>
            <span role="columnheader" />
          </div>
          {o.lines.length === 0 ? <p className="w-muted sales-empty">{t("sales.noLines")}</p> : null}
          {o.lines.map((l) => (
            <div key={l.no} className="sales-line" role="row">
              <span role="cell" className="sales-product">
                <span className="w-num">{l.productCode}</span> {l.productName}
                {l.version ? <span className="w-muted"> · {t("sales.version", { version: l.version })}</span> : null}
              </span>
              <span role="cell" className="num w-num" aria-label={t("sales.cell.qty", { qty: formatNumber(l.qty) })}>
                <span className="sales-wide">{formatNumber(l.qty)}</span>
                <span className="sales-narrow">{t("sales.cell.qty", { qty: formatNumber(l.qty) })}</span>
              </span>
              <span role="cell" className="num w-num">
                {formatMoney(l.unitPrice, "UAH")}
              </span>
              <span role="cell" className="num w-num" aria-label={t("sales.cell.left", { qty: formatNumber(l.remaining) })}>
                <span className="sales-wide">{formatNumber(l.remaining)}</span>
                <span className="sales-narrow">{t("sales.cell.left", { qty: formatNumber(l.remaining) })}</span>
              </span>
              <span role="cell" className="sales-line-act">
                {confirmed && l.remaining > 0 && can("sales.ship") ? (
                  <Button variant="primary" onClick={() => setShipLine(l)}>
                    {t("sales.ship")}
                  </Button>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      </section>

      {draft && can("sales.order") ? <AddLine order={o} onDone={(next) => queryClient.setQueryData(salesOrderQuery(id).queryKey, next)} onError={fail} /> : null}

      {o.shipped.length > 0 ? (
        <section className="stock-section" aria-labelledby="sales-shipped">
          <h2 id="sales-shipped" className="desk-group-title">
            {t("sales.shipped")}
          </h2>
          <ul className="sales-shipped">
            {o.shipped.map((s) => (
              <li key={`${s.serial}-${s.shippedOn}`} className="sales-shipped-row">
                <span className="w-num sales-serial">{s.serial}</span>
                <span className="w-muted">
                  {[
                    t("sales.shippedOn", { date: formatDate(s.shippedOn) }),
                    s.returnedOn ? t("sales.returnedOn", { date: formatDate(s.returnedOn) }) : null,
                    s.refusedOn ? t("sales.refusedOn", { date: formatDate(s.refusedOn) }) : null,
                    s.reshippedOn ? t("sales.reshippedOn", { date: formatDate(s.reshippedOn) }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className="w-num num">{formatMoney(s.revenue, "UAH")}</span>
                <span className="sales-shipped-act">
                  {!s.returnedOn && can("sales.order") ? (
                    <Button
                     
                      variant="ghost"
                      disabled={run.isPending}
                      onClick={() =>
                        setConfirm({
                          title: t("sales.return.title", { serial: s.serial }),
                          body: t("sales.return.body"),
                          action: t("sales.return"),
                          run: () => act(() => salesApi.returnUnit(s.serial), { tone: "success", title: t("sales.returned", { serial: s.serial }) }),
                        })
                      }
                    >
                      {t("sales.return")}
                    </Button>
                  ) : null}
                  {s.returnedOn && !s.refusedOn && !s.reshippedOn && can("sales.ship") ? (
                    <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => salesApi.reship(s.serial), { tone: "success", title: t("sales.reshipped", { serial: s.serial }) })}>
                      {t("sales.reship")}
                    </Button>
                  ) : null}
                  {s.returnedOn && !s.refusedOn && !s.reshippedOn && can("sales.refusal") ? (
                    <Button variant="ghost" onClick={() => setRefusal(s.serial)}>
                      {t("sales.refusal")}
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="order-actions">
        {draft && can("sales.order") ? (
          <Button variant="primary" disabled={run.isPending || o.lines.length === 0} onClick={() => act(() => salesApi.confirm(o.id), { tone: "success", title: t("sales.confirmed") })}>
            {t("sales.confirm")}
          </Button>
        ) : null}
        {(draft || confirmed) && can("sales.order") ? (
          <Button
            variant="ghost"
            disabled={run.isPending}
            onClick={() =>
              setConfirm({
                title: t("sales.cancel.title", { order: o.number }),
                body: t("sales.cancel.body"),
                action: t("sales.cancel"),
                run: () => act(() => salesApi.cancel(o.id)),
              })
            }
          >
            {t("sales.cancel")}
          </Button>
        ) : null}
      </div>

      {shipLine ? (
        <ShipDialog
          order={o}
          line={shipLine}
          onClose={() => setShipLine(null)}
          onShipped={(serial, warnings) => {
            setShipLine(null);
            const warning = warnings.map((w) => serverErrorText(w, { serial }) ?? w).join(" ");
            refresh({ tone: warning ? "warning" : "success", title: [t("sales.shippedOk", { serial }), warning].filter(Boolean).join(" ") });
          }}
        />
      ) : null}
      {refusal ? (
        <RefusalDialog
          serial={refusal}
          onClose={() => setRefusal(null)}
          onDone={() => {
            const serial = refusal;
            setRefusal(null);
            refresh({ tone: "success", title: t("sales.refused", { serial }) });
          }}
        />
      ) : null}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="order-meta-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Строка заказа: изделие из справочника, версия, количество и цена без ПДВ. */
function AddLine({ order, onDone, onError }: { order: SalesOrder; onDone: (o: SalesOrder) => void; onError: (e: unknown) => void }) {
  const [product, setProduct] = useState<ProductRef | null>(null);
  const [version, setVersion] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const add = useMutation({ mutationFn: () => salesApi.addLine(order.id, { productId: product?.id ?? "", version: version.trim() || null, qty: parseQty(qty) ?? 0, unitPrice: parseQty(price) ?? -1 }) });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    add.mutate(undefined, {
      onSuccess: (o) => {
        onDone(o);
        setProduct(null);
        setVersion("");
        setQty("1");
        setPrice("");
      },
      onError,
    });
  };

  return (
    <section className="stock-section" aria-labelledby="sales-add">
      <h2 id="sales-add" className="desk-group-title">
        {t("sales.addLine")}
      </h2>
      {product ? (
        <form className="stock-form sales-add" onSubmit={submit}>
          <p className="sales-picked">
            <span className="w-num">{product.sku}</span> {product.name}{" "}
            <Button variant="ghost" type="button" onClick={() => setProduct(null)}>
              {t("sales.change")}
            </Button>
          </p>
          <TextField label={t("sales.versionField")} hint={t("sales.versionHint")} value={version} onChange={(e) => setVersion(e.target.value)} autoComplete="off" />
          <TextField label={t("sales.qty")} inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} required />
          <TextField label={t("sales.priceField")} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} required />
          <Button type="submit" variant="primary" disabled={add.isPending}>
            {t("sales.add")}
          </Button>
        </form>
      ) : (
        <ProductSearch onPick={setProduct} />
      )}
    </section>
  );
}

/**
 * Выбор серийника под строку: только «Готова продукція» той же версии
 * (ПС-04), карточками с серийником и полкой; скан QR или ввод тоже годится,
 * чужой серийник сервер отклонит с причиной.
 */
function ShipDialog({ order, line, onClose, onShipped }: { order: SalesOrder; line: SalesLine; onClose: () => void; onShipped: (serial: string, warnings: string[]) => void }) {
  const candidates = useQuery(candidatesQuery(order.id, line.no));
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<NoticeState | null>(null);
  const [warn, setWarn] = useState<Candidate | null>(null);
  const ship = useMutation({ mutationFn: (serial: string) => salesApi.ship(order.id, line.no, serial) });
  const go = (serial: string) => {
    setError(null);
    ship.mutate(serial, { onSuccess: (r) => onShipped(r.serial, r.warnings), onError: (e) => setError(errorNotice(e)) });
  };
  const pick = (c: Candidate) => (c.routeUnclosed ? setWarn(c) : go(c.serial));

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t("sales.ship.title", { product: `${line.productCode} ${line.productName}` })}
      description={t("sales.ship.body")}
    >
      {error ? <Notice tone="danger" title={error.title} /> : null}
      {warn ? (
        <Notice tone="warning" title={t("sales.ship.unclosed", { serial: warn.serial })}>
          <div className="notice-actions">
            <Button variant="primary" disabled={ship.isPending} onClick={() => go(warn.serial)}>
              {t("sales.ship.anyway")}
            </Button>
            <Button variant="ghost" onClick={() => setWarn(null)}>
              {t("sales.ship.other")}
            </Button>
          </div>
        </Notice>
      ) : null}
      <form
        className="sales-scan"
        onSubmit={(e) => {
          e.preventDefault();
          const s = typed.trim();
          if (!s) return;
          const c = candidates.data?.find((x) => x.serial.toUpperCase() === s.toUpperCase());
          if (c) pick(c);
          else go(s);
        }}
      >
        <TextField label={t("sales.ship.scan")} variant="scan" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
      </form>
      {candidates.data && candidates.data.length === 0 ? <p className="w-muted">{t("sales.ship.none")}</p> : null}
      <ul className="sales-candidates">
        {candidates.data?.map((c) => (
          <li key={c.serial}>
            <button type="button" className="sales-candidate" disabled={ship.isPending} onClick={() => pick(c)}>
              <span className="w-num sales-serial">{c.serial}</span>
              <span className="w-muted">{c.place}</span>
              {c.routeUnclosed ? <Badge tone="warning">{t("sales.routeUnclosed")}</Badge> : null}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** «Відмова покупця» (H12): только «Керівництво», с причиной; выручка сторнируется датой отказа. */
function RefusalDialog({ serial, onClose, onDone }: { serial: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<NoticeState | null>(null);
  const refuse = useMutation({ mutationFn: () => salesApi.refusal(serial, reason.trim()) });
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t("sales.refusal.title", { serial })}
      description={t("sales.refusal.body")}
      footer={
        <Button
          variant="primary"
          disabled={refuse.isPending || !reason.trim()}
          onClick={() => refuse.mutate(undefined, { onSuccess: onDone, onError: (e) => setError(errorNotice(e)) })}
        >
          {t("sales.refusal")}
        </Button>
      }
    >
      {error ? <Notice tone="danger" title={error.title} /> : null}
      <TextField label={t("sales.refusal.reason")} value={reason} onChange={(e) => setReason(e.target.value)} required />
    </Dialog>
  );
}
